import React, { useState } from 'react';
import { Star } from 'lucide-react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { toast } from 'sonner';
import { refreshNotifications } from './NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function RatingModal({ open, onOpenChange, errand, onSuccess }) {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [review, setReview] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating');
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/ratings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ errand_id: errand.errand_id, rating, review: review.trim() || null })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Rating failed');
      }

      toast.success('Thanks for rating!');
      onSuccess?.();
      onOpenChange(false);
      setRating(0);
      setReview('');
      refreshNotifications();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!errand) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="rating-modal" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-teal-700">Rate Your Runner</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 mt-4">
          <div className="text-center">
            <p className="text-slate-600 mb-4">How was your experience with</p>
            <p className="text-lg font-bold text-slate-900">{errand.runner_name}?</p>
          </div>

          <div className="flex items-center justify-center gap-2">
            {[1, 2, 3, 4, 5].map((star) => (
              <button
                key={star}
                data-testid={`star-${star}`}
                onMouseEnter={() => setHover(star)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setRating(star)}
                className="btn-hover"
              >
                <Star
                  className={`w-10 h-10 transition-colors ${
                    (hover || rating) >= star
                      ? 'fill-orange-500 text-orange-500'
                      : 'text-slate-300'
                  }`}
                  strokeWidth={1.5}
                />
              </button>
            ))}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Leave a review (optional)</label>
            <Textarea
              data-testid="review-input"
              value={review}
              onChange={(e) => setReview(e.target.value)}
              className="bg-slate-50 border-slate-200 focus:ring-2 focus:ring-teal-700/20 focus:border-teal-700 rounded-lg"
              placeholder="Share your experience..."
              rows={3}
            />
          </div>

          <Button
            data-testid="submit-rating-btn"
            onClick={handleSubmit}
            disabled={submitting || rating === 0}
            className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-full py-3 font-medium shadow-lg btn-hover"
          >
            {submitting ? 'Submitting...' : 'Submit Rating'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
